import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { API_BASE_URL, getAuthHeaders, getStoredUser } from './auth';
import { EmptyPane, ErrorBanner, LoadingPane, PageHeader } from './components/page-elements';

const roleChipColor = (role) => {
  if (role === 'admin') return 'error';
  if (role === 'internal') return 'info';
  return 'default';
};

const parseResponse = async (response) => {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
};

const Users = () => {
  const currentUser = useMemo(() => getStoredUser(), []);
  const isAdmin = currentUser?.role === 'admin';

  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingUserId, setEditingUserId] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'customer',
    customerName: '',
    isActive: true,
    receiveBols: false,
  });

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    customerName: '',
    email: '',
    password: '',
  });

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [usersRes, customersRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/users`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/api/customers`, { headers: getAuthHeaders() }),
      ]);

      if (!usersRes.ok) {
        const payload = await parseResponse(usersRes);
        throw new Error(payload.message || 'Failed to load users');
      }

      if (!customersRes.ok) {
        const payload = await parseResponse(customersRes);
        throw new Error(payload.message || 'Failed to load customers');
      }

      const usersData = await usersRes.json();
      const customersData = await customersRes.json();

      setUsers(Array.isArray(usersData) ? usersData : []);
      setCustomers(Array.isArray(customersData) ? customersData : []);
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    loadData();
  }, [isAdmin]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateInternalUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.firstName || !form.lastName || !form.customerName || !form.email) {
      setError('First name, last name, customer, and email are required');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/internal`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify(form),
      });

      const payload = await parseResponse(response);
      if (!response.ok) {
        throw new Error(payload.message || 'Failed to create internal user');
      }

      setSuccess(payload.message || 'Internal user created successfully');
      setForm({
        firstName: '',
        lastName: '',
        customerName: '',
        email: '',
        password: '',
      });
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to create internal user');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEdit = (user) => {
    setEditingUserId(String(user._id));
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      role: user.role || 'customer',
      customerName: String(user.customerName?._id || user.customerName || ''),
      isActive: user.isActive !== false,
      receiveBols: Boolean(user.receiveBols),
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${editingUserId}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify(editForm),
      });
      const payload = await parseResponse(response);
      if (!response.ok) {
        throw new Error(payload.message || 'Failed to update user');
      }
      setSuccess('User updated successfully');
      setEditOpen(false);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleSendReset = async (user) => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ email: user.email }),
      });
      const payload = await parseResponse(response);
      if (!response.ok) {
        throw new Error(payload.message || 'Failed to send reset link');
      }
      setSuccess(`Reset link sent (if email is configured) to ${user.email}`);
    } catch (err) {
      setError(err.message || 'Failed to send reset link');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (String(user._id) === String(currentUser?._id)) {
      setError('You cannot delete your own admin account.');
      return;
    }

    const confirmed = window.confirm(`Delete user ${user.email}? This cannot be undone.`);
    if (!confirmed) return;

    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${user._id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const payload = await parseResponse(response);
      if (!response.ok) {
        throw new Error(payload.message || 'Failed to delete user');
      }
      setSuccess('User deleted successfully');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to delete user');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingPane label="Loading users..." />;

  if (!isAdmin) {
    return (
      <Box p={2}>
        <Alert severity="error">Access forbidden: admin users only.</Alert>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <PageHeader
        title="User Administration"
        subtitle="Admin-only page to review all users and create internal users."
      />

      <ErrorBanner message={error} />
      {success ? <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert> : null}

      <Card sx={{ mb: 2, borderRadius: 2, border: '1px solid var(--ui-border)' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Create Internal User
          </Typography>
          <Box component="form" onSubmit={handleCreateInternalUser}>
            <Grid container spacing={1.2}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="firstName"
                  label="First Name"
                  value={form.firstName}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="lastName"
                  label="Last Name"
                  value={form.lastName}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="password"
                  label="Temporary Password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  helperText="Optional. Leave blank to auto-generate and email."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="internal-user-customer-label">Customer</InputLabel>
                  <Select
                    labelId="internal-user-customer-label"
                    name="customerName"
                    value={form.customerName}
                    label="Customer"
                    onChange={handleChange}
                  >
                    {customers.map((customer) => (
                      <MenuItem key={customer._id} value={customer._id}>
                        {customer.customerName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="flex-end">
                  <Button type="submit" variant="contained" disabled={saving}>
                    {saving ? 'Creating...' : 'Create Internal User'}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {users.length === 0 ? (
        <EmptyPane title="No users found" subtitle="Create your first internal user above." />
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid var(--ui-border)' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Customer</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Receive BOLs</TableCell>
              <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user._id}>
                  <TableCell>{`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A'}</TableCell>
                  <TableCell>{user.email || 'N/A'}</TableCell>
                  <TableCell>{user.customerName?.customerName || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip label={user.role || 'customer'} color={roleChipColor(user.role)} size="small" />
                  </TableCell>
                  <TableCell>{user.receiveBols ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive === false ? 'Inactive' : 'Active'}
                      color={user.isActive === false ? 'default' : 'success'}
                      size="small"
                      variant={user.isActive === false ? 'outlined' : 'filled'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" variant="outlined" onClick={() => handleOpenEdit(user)} disabled={saving}>
                        Edit
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => handleSendReset(user)} disabled={saving}>
                        Send Reset Link
                      </Button>
                      <Button size="small" color="error" variant="outlined" onClick={() => handleDeleteUser(user)} disabled={saving}>
                        Delete
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.2} sx={{ mt: 0.25 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                value={editForm.firstName}
                onChange={(e) => setEditForm((prev) => ({ ...prev, firstName: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                value={editForm.lastName}
                onChange={(e) => setEditForm((prev) => ({ ...prev, lastName: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="edit-role-label">Role</InputLabel>
                <Select
                  labelId="edit-role-label"
                  value={editForm.role}
                  label="Role"
                  onChange={(e) => setEditForm((prev) => ({ ...prev, role: e.target.value }))}
                >
                  <MenuItem value="customer">Customer</MenuItem>
                  <MenuItem value="internal">Internal</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="edit-active-label">Status</InputLabel>
                <Select
                  labelId="edit-active-label"
                  value={editForm.isActive ? 'active' : 'inactive'}
                  label="Status"
                  onChange={(e) => setEditForm((prev) => ({ ...prev, isActive: e.target.value === 'active' }))}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(editForm.receiveBols)}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, receiveBols: Boolean(e.target.checked) }))}
                  />
                }
                label="Receive completed BOL emails"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="edit-customer-label">Customer</InputLabel>
                <Select
                  labelId="edit-customer-label"
                  value={editForm.customerName}
                  label="Customer"
                  onChange={(e) => setEditForm((prev) => ({ ...prev, customerName: e.target.value }))}
                >
                  {customers.map((customer) => (
                    <MenuItem key={customer._id} value={String(customer._id)}>
                      {customer.customerName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
